import React, { useMemo } from 'react';
import {
  Boxes,
  Calendar,
  ChartPie,
  Inbox,
  MonitorCog,
  Settings2,
  ShoppingBasket,
  Store,
  ChevronLeft,
} from 'lucide-react';

import { NavMain } from '@/components/navbar/nav-main';
import { NavUser } from '@/components/navbar/nav-user';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { authService } from '@socialflows/shared-lib';
import type { User } from '@socialflows/shared-lib';
import { useQuery } from '@tanstack/react-query';

const data = {
  navSections: [
    {
      items: [
        { title: 'Orders', url: '/e-commerce/orders', icon: ShoppingBasket },
        { title: 'Products', url: '/e-commerce/products', icon: Boxes },
        { title: 'Catalog', url: '/e-commerce/categories', icon: Store },
      ],
    },
    {
      items: [
        { title: 'Inbox', url: '/social-manager/inbox', icon: Inbox },
        { title: 'Publisher', url: '/social-manager/publisher', icon: Calendar },
      ],
    },
    {
      items: [
        { title: 'Analytics', url: '/analytics', icon: ChartPie },
        { title: 'Customization', url: '/customization/presets', icon: MonitorCog },
        { title: 'Settings', url: '/settings/general', icon: Settings2 },
      ],
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { toggleSidebar } = useSidebar();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['auth-profile'],
    queryFn: () => authService.getProfile(),
  });

  const user = useMemo<User | null>(() => {
    if (!profile) return null;
    return {
      id: profile.id,
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      email: profile.email,
      phone: profile.phone || '',
      role: profile.role,
      accountId: profile.account_id,
      isEnabled: false,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at,
      isVip: profile.is_vip,
    };
  }, [profile]);

  const getUserInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getDisplayName = (firstName: string, lastName: string) => {
    return `${firstName} ${lastName}`.trim();
  };

  const displayUser = user
    ? {
        name: getDisplayName(user.firstName, user.lastName),
        email: user.email,
        avatar: '',
        initials: getUserInitials(user.firstName, user.lastName),
      }
    : isLoading
      ? {
          name: 'Loading...',
          email: '',
          avatar: '',
          initials: '...',
        }
      : {
          name: 'User',
          email: 'user@example.com',
          avatar: '',
          initials: 'U',
        };

  return (
    <Sidebar className="z-50" collapsible="icon" {...props}>
      <SidebarHeader className="border-b">
        <NavUser user={displayUser} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain sections={data.navSections} />
      </SidebarContent>
      <SidebarFooter className="border-t">
        <SidebarMenuItem>
          <SidebarMenuButton onClick={toggleSidebar}>
            <ChevronLeft className="transition-transform duration-200 group-data-[state=collapsed]:rotate-180" />
            <span className="text-md group-data-[state=collapsed]:hidden">Collapse</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
